import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, WRITE_ROLES } from "../lib/auth";
import { useApiData } from "../lib/hooks";
import { ErrorMessage } from "../components/ErrorMessage";
import { Field } from "../components/Field";
import { Loading } from "../components/Loading";
import { EmptyState } from "../components/EmptyState";
import { BOW_STYLE_OPTIONS, BOW_STYLE_LABEL } from "../lib/bowStyles";

// Bow styles come from shared/enums.json via lib/bowStyles.js (#2048). This
// page used to carry its own copy in a different order (barebow before
// compound) — the shared order now wins.

function LoadoutForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [bowStyle, setBowStyle] = useState(initial?.bow_style ?? null);
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
  const [defaultInnerTen, setDefaultInnerTen] = useState(initial?.default_inner_ten ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), description: description.trim() || null, bow_style: bowStyle, is_default: isDefault, default_inner_ten: defaultInnerTen });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="eq-form" onSubmit={submit}>
      <Field variant="eq" htmlFor="eq-name" label="Name">
        <input
          id="eq-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Competition recurve"
          maxLength={100}
          required
        />
      </Field>
      <Field
        variant="eq"
        htmlFor="eq-desc"
        label="Description "
        labelExtra={<span className="eq-hint">({255 - description.length} chars left)</span>}
      >
        <textarea
          id="eq-desc"
          className="input profile-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={255}
          placeholder="Limb weight, draw length, sight marks, stabiliser setup…"
        />
      </Field>
      <div className="field field--eq">
        <label className="eq-label">Bow style</label>
        <div className="eq-bow-style-row">
          <button
            type="button"
            className={"eq-bow-btn" + (bowStyle === null ? " eq-bow-btn--on" : "")}
            onClick={() => setBowStyle(null)}
          >Any</button>
          {BOW_STYLE_OPTIONS.map((s) => (
            <button
              key={s.value}
              type="button"
              className={"eq-bow-btn" + (bowStyle === s.value ? " eq-bow-btn--on" : "")}
              onClick={() => setBowStyle(s.value)}
            >{s.label}</button>
          ))}
        </div>
      </div>
      <label className="profile-toggle u-mb-12">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
        <span>Set as default loadout</span>
      </label>
      <label className="profile-toggle u-mb-12">
        <input
          type="checkbox"
          checked={defaultInnerTen}
          onChange={(e) => setDefaultInnerTen(e.target.checked)}
        />
        <span>Default inner-ten scoring</span>
      </label>
      <ErrorMessage message={error} />
      <div className="eq-form-actions">
        <button type="submit" className="btn primary" disabled={saving || !name.trim()}>
          {saving ? "Saving…" : initial ? "Save changes" : "Add loadout"}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function Equipment() {
  // Mirrors the backend write gate (#1857): only WRITE_ROLES members of the
  // active archer may mutate loadouts — viewers get a read-only list.
  const { activeArcher, archers } = useAuth();
  const role = archers.find((m) => m.archer.id === activeArcher?.id)?.role;
  const canEdit = WRITE_ROLES.includes(role);

  const { data: fetchedLoadouts, loading, error } = useApiData(
    () => api.getLoadouts(),
    [],
  );
  const [loadouts, setLoadouts] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (fetchedLoadouts !== null) setLoadouts(fetchedLoadouts);
  }, [fetchedLoadouts]);

  async function handleAdd(payload) {
    const lo = await api.createLoadout(payload);
    setLoadouts((prev) => {
      const updated = payload.is_default
        ? prev.map((l) => ({ ...l, is_default: false }))
        : prev;
      return [...updated, lo];
    });
    setAdding(false);
  }

  async function handleUpdate(id, payload) {
    const lo = await api.updateLoadout(id, payload);
    setLoadouts((prev) => {
      let updated = prev.map((l) => l.id === id ? lo : l);
      if (payload.is_default) updated = updated.map((l) => l.id !== id ? { ...l, is_default: false } : l);
      return updated;
    });
    setEditingId(null);
  }

  async function handleDelete(id) {
    await api.deleteLoadout(id);
    setLoadouts((prev) => prev.filter((l) => l.id !== id));
    setConfirmDeleteId(null);
  }

  async function handleSetDefault(id) {
    await api.updateLoadout(id, { is_default: true });
    setLoadouts((prev) => prev.map((l) => ({ ...l, is_default: l.id === id })));
  }

  return (
    <main className="main">
      <div className="main-head">
        <div>
          <h1>Equipment</h1>
          <div className="greeting">
            {canEdit ? "Manage your gear loadouts" : "Viewing — read only."}
          </div>
        </div>
      </div>

      <ErrorMessage message={error} />

      {loading && <Loading compact />}

      {loadouts !== null && (
        <div className="eq-list">
          {loadouts.length === 0 && !adding && (
            <EmptyState
              variant="inline"
              message="No loadouts yet. Add one to attach gear notes to your rounds."
            />
          )}

          {loadouts.map((lo) => (
            <div key={lo.id} className={"eq-card" + (lo.is_default ? " eq-card--default" : "")}>
              {editingId === lo.id ? (
                <LoadoutForm
                  initial={lo}
                  onSave={(p) => handleUpdate(lo.id, p)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <>
                  <div className="eq-card-head">
                    <div>
                      <span className="eq-card-name">{lo.name}</span>
                      {lo.is_default && <span className="eq-card-badge u-ml-8">Default</span>}
                    </div>
                    {canEdit && (
                    <div className="eq-card-actions">
                      {!lo.is_default && (
                        <button className="btn-sm" onClick={() => handleSetDefault(lo.id)}>
                          Set default
                        </button>
                      )}
                      <button className="btn-sm" onClick={() => setEditingId(lo.id)}>Edit</button>
                      {confirmDeleteId === lo.id ? (
                        <>
                          <button className="btn-sm btn-sm-danger" onClick={() => handleDelete(lo.id)}>Confirm</button>
                          <button className="btn-sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                        </>
                      ) : (
                        <button className="btn-sm btn-sm-danger" onClick={() => setConfirmDeleteId(lo.id)}>Delete</button>
                      )}
                    </div>
                    )}
                  </div>
                  {(lo.bow_style || lo.description) && (
                    <div className="eq-card-desc">
                      {lo.bow_style && (
                        <span className="eq-card-bow">{BOW_STYLE_LABEL[lo.bow_style] ?? lo.bow_style}</span>
                      )}
                      {lo.bow_style && lo.description && " · "}
                      {lo.description}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {canEdit && (adding ? (
            <div className="eq-card">
              <LoadoutForm onSave={handleAdd} onCancel={() => setAdding(false)} />
            </div>
          ) : (
            <button className="btn u-mt-8" onClick={() => setAdding(true)}>
              + Add loadout
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
